import { estIdentiteAgent } from '@/lib/identiteAgent'
import { AudioLines, Bot, PhoneIncoming, PhoneOutgoing } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router'
import { format } from 'date-fns'
import { enUS, fr } from 'date-fns/locale'
import { cn } from '@/lib/utils'
import type { CompanyCallLog } from '@/api/callLogs'
import { getStatusStyle } from './historyHelpers'
import { InlineRecordingPlayer } from './InlineRecordingPlayer'

interface Props {
  logs: CompanyCallLog[]
  companyId: number
  expandedLogId: number | null
  onToggleExpand: (logId: number | null) => void
}

/** Vue tableau pour desktop (md+) — flat, sans card. */
export function CallLogsTable({
  logs,
  companyId,
  expandedLogId,
  onToggleExpand,
}: Props) {
  const { t } = useTranslation()
  const { locale = 'fr' } = useParams<{ locale: string }>()
  const dateLocale = locale === 'fr' ? fr : enUS

  return (
    <div className="hidden md:block overflow-x-auto -mx-2">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-slate-200 dark:border-slate-800 text-left">
            <th className="px-2 py-3 font-semibold text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">
              {t('manager.history.table.when')}
            </th>
            <th className="px-2 py-3 font-semibold text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">
              {t('manager.history.table.direction')}
            </th>
            <th className="px-2 py-3 font-semibold text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">
              {t('manager.history.table.from')}
            </th>
            <th className="px-2 py-3 font-semibold text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">
              {t('manager.history.table.to')}
            </th>
            <th className="px-2 py-3 font-semibold text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">
              {t('manager.history.table.status')}
            </th>
            <th className="px-2 py-3 font-semibold text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400 text-right">
              {t('manager.history.table.duration')}
            </th>
            <th className="px-2 py-3 font-semibold text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400 text-center">
              {t('manager.history.table.recording')}
            </th>
          </tr>
        </thead>
        <tbody>
          {logs.map((log) => (
            <CallLogRow
              key={log.id}
              log={log}
              dateLocale={dateLocale}
              isExpanded={expandedLogId === log.id}
              onToggleRecording={() =>
                onToggleExpand(expandedLogId === log.id ? null : log.id)
              }
              companyId={companyId}
              onCloseExpanded={() => onToggleExpand(null)}
            />
          ))}
        </tbody>
      </table>
    </div>
  )
}

function CallLogRow({
  log,
  dateLocale,
  isExpanded,
  onToggleRecording,
  companyId,
  onCloseExpanded,
}: {
  log: CompanyCallLog
  dateLocale: Locale
  isExpanded: boolean
  onToggleRecording: () => void
  companyId: number
  onCloseExpanded: () => void
}) {
  const { t } = useTranslation()
  const style = getStatusStyle(log.status)
  const isInbound = log.direction === 'inbound'
  const when = format(new Date(log.started_at), 'PPp', { locale: dateLocale })

  return (
    <>
    <tr
      className={cn(
        'border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors',
        isExpanded && 'bg-indigo-50/40 dark:bg-indigo-500/5',
      )}
    >
      <td className="px-2 py-3 text-slate-700 dark:text-slate-300 whitespace-nowrap">
        {when}
      </td>
      <td className="px-2 py-3">
        <span
          className={cn(
            'inline-flex items-center gap-1.5 text-xs font-medium',
            isInbound
              ? 'text-emerald-700 dark:text-emerald-300'
              : 'text-indigo-700 dark:text-indigo-300',
          )}
        >
          {isInbound ? (
            <PhoneIncoming className="h-3.5 w-3.5" />
          ) : (
            <PhoneOutgoing className="h-3.5 w-3.5" />
          )}
          {isInbound
            ? t('manager.history.table.inbound')
            : t('manager.history.table.outbound')}
        </span>
      </td>
      <td className="px-2 py-3">
        <CallParty
          isAgent={!isInbound && !!log.agent_name}
          agentName={log.agent_name}
          phone={log.from_number}
          fallbackLabel={t('manager.history.table.webCall')}
        />
      </td>
      <td className="px-2 py-3">
        <CallParty
          isAgent={isInbound && !!log.agent_name}
          agentName={log.agent_name}
          phone={log.display_destination || log.to_number}
          isAi={log.answer_type === 'ai'}
          fallbackLabel={
            log.answer_type === 'ai'
              ? t('manager.history.table.answeredByAi')
              : log.answer_type === 'web'
                ? t('manager.history.table.inboundWeb')
                : t('manager.history.table.answeredByAgent')
          }
        />
      </td>
      <td className="px-2 py-3">
        <span
          className={cn(
            'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium',
            style.className,
          )}
        >
          <span
            className={cn(
              'w-1.5 h-1.5 rounded-full',
              style.dotClass,
              style.pulse && 'animate-pulse',
            )}
          />
          {t(`manager.history.statuses.${style.labelKey}`)}
        </span>
      </td>
      <td className="px-2 py-3 text-right font-mono text-sm text-slate-700 dark:text-slate-300 whitespace-nowrap">
        {log.duration_formatted || '0s'}
      </td>
      <td className="px-2 py-3 text-center">
        {/* Closed to this person: shown like a call without audio. */}
        {!log.recording_locked && (log.has_recordings || log.ai_audio_url) ? (
          <button
            type="button"
            onClick={onToggleRecording}
            title={t('manager.history.table.hasRecording')}
            className={cn(
              'inline-flex items-center justify-center w-7 h-7 rounded-full transition-colors',
              isExpanded
                ? 'bg-indigo-500 text-white shadow-md shadow-indigo-500/30'
                : 'bg-emerald-50 dark:bg-emerald-500/10 text-emerald-600 dark:text-emerald-300 hover:bg-emerald-100 dark:hover:bg-emerald-500/20',
            )}
          >
            <AudioLines className="h-3.5 w-3.5" />
          </button>
        ) : (
          <span className="text-slate-300 dark:text-slate-600">—</span>
        )}
      </td>
    </tr>
    {isExpanded && (
      <tr className="border-b border-slate-100 dark:border-slate-800 bg-indigo-50/40 dark:bg-indigo-500/5">
        <td colSpan={7} className="px-3 py-2">
          <div className="flex items-center">
            <InlineRecordingPlayer
              companyId={companyId}
              callId={log.id}
              aiAudioUrl={log.ai_audio_url}
              onClose={onCloseExpanded}
            />
          </div>
        </td>
      </tr>
    )}
    </>
  )
}

interface CallPartyProps {
  isAgent: boolean
  agentName: string | null
  phone: string | null
  fallbackLabel: string
  /** L'appel a été pris en charge par un agent IA : badge « IA » à côté du nom. */
  isAi?: boolean
}

function CallParty({
  isAgent,
  agentName,
  phone,
  fallbackLabel,
  isAi = false,
}: CallPartyProps) {
  const { t } = useTranslation()
  if (isAgent && agentName) {
    return (
      <div>
        <div className="font-medium text-slate-900 dark:text-white text-sm flex items-center gap-1.5">
          {agentName}
          {isAi && (
            <span className="inline-flex items-center gap-0.5 px-1.5 py-px rounded-full text-[10px] font-semibold bg-violet-50 text-violet-700 dark:bg-violet-500/15 dark:text-violet-300">
              <Bot className="w-2.5 h-2.5" />
              {t('manager.history.table.aiBadge')}
            </span>
          )}
        </div>
        <div className="text-xs text-slate-400 dark:text-slate-500">
          {fallbackLabel}
        </div>
      </div>
    )
  }
  if (estIdentiteAgent(phone)) {
    return <span className="text-sm text-slate-700 dark:text-slate-300">{agentName || fallbackLabel}</span>
  }
  return (
    <span className="font-mono text-sm text-slate-700 dark:text-slate-300">
      {phone || '—'}
    </span>
  )
}

type Locale = typeof fr
